import { Menu as BaseMenu } from "@base-ui/react/menu";
import type { ReactElement, ReactNode } from "react";
import { cx, usePortalContainer } from "../presentation.tsx";
import { Icon, type IconName } from "../icons.tsx";

export interface MenuItem {
  id: string;
  label: ReactNode;
  icon?: IconName;
  /** Small text after the label: a shortcut, a count, "Current". */
  hint?: ReactNode;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
}

export interface MenuProps {
  /** The button that opens the menu. */
  trigger: ReactElement;
  /** Shown above the items, not focusable: who is signed in, which workspace. */
  header?: ReactNode;
  /** Items in groups; groups are separated by a rule. */
  groups: MenuItem[][];
  align?: "start" | "center" | "end";
  side?: "top" | "bottom" | "left" | "right";
  className?: string;
}

/** A button's dropdown of actions (account menu, workspace switcher). Arrow keys, type-ahead, Escape. */
export function Menu({ trigger, header, groups, align = "end", side = "bottom", className }: MenuProps) {
  const container = usePortalContainer();
  const shown = groups.filter((g) => g.length > 0);
  return (
    <BaseMenu.Root>
      <BaseMenu.Trigger render={trigger} />
      <BaseMenu.Portal container={container}>
        <BaseMenu.Positioner className="pmu-menu__positioner" sideOffset={8} align={align} side={side}>
          <BaseMenu.Popup className={cx("pmu-popup pmu-menu", className)}>
            {header && <div className="pmu-menu__header">{header}</div>}
            {shown.map((g, i) => (
              <div key={i} className="pmu-menu__group" role="group">
                {i > 0 || header ? <div className="pmu-menu__rule" role="separator" /> : null}
                {g.map((item) => (
                  <BaseMenu.Item key={item.id} className={cx("pmu-menu__item", item.danger && "pmu-menu__item--danger")} disabled={item.disabled} onClick={item.onSelect}>
                    {item.icon ? <Icon name={item.icon} /> : <span />}
                    <span className="pmu-menu__label">{item.label}</span>
                    {item.hint != null && <span className="pmu-menu__hint">{item.hint}</span>}
                  </BaseMenu.Item>
                ))}
              </div>
            ))}
          </BaseMenu.Popup>
        </BaseMenu.Positioner>
      </BaseMenu.Portal>
    </BaseMenu.Root>
  );
}
